import Checkbox from 'src/components/Checkbox';
import FontAwesomeIcon from 'src/components/Icon';
import GroupedHeaderControls from 'src/pages/ServiceRequests/GroupedHeaderControls';
import { convertTime } from 'src/helpers/time';
import Label from 'src/components/Label';
import { sortByDate } from 'src/components/Table/sorting';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { ICONS, PATHS, SuccessMessages } from 'src/common/constants';
import IcoMoon from 'src/components/IcoMoon';
import Status from 'src/components/Status';
import CategoryPill from 'src/components/CategoryPill/CategoryPill';
import { displayStatusLabel } from 'src/utils/serviceRequestStatus/displayStatusLabel';
import { getDaysOpenPill } from 'src/utils/serviceRequestDaysOpen/getDaysOpenPill';
import { Attachments } from './Attachments';
import { DescriptionCell } from './DescriptionCell';
import ExpandableText from 'src/components/ExpandableText/ExpandableText';
import { downloadDocument } from 'src/helpers/DownloadDoc';
import { ToastInfo } from 'src/helpers/validationHelper';
import { ITableColumn } from 'src/components/Table/types';
import './service-requests-table-cells.scss';

const isValidDate = (date: any) => date !== null && moment(date).year() > 2020;

// API delivers `tags` as a JSON-stringified array (legacy/workaround).
// Parse at the rendering boundary; bail to [] on malformed payloads.
const parseTags = (raw: unknown): string[] => {
  if (!raw || typeof raw !== 'string') {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
};

export interface CreateTableColumnsParams {
  allSelected: boolean;
  onSelectAll: () => void;
  expandedGroups: any[];
  setExpandedGroups: (groups: any[]) => void;
  selectedServiceRequests: string[];
  onSelectGroup: (ids: any) => void;
  onDeleteGroup: (id: any) => void;
  onSelectServiceRequest: (id: any) => void;
  onRemoveFromGroup: (itemId: any, groupId: any) => void;
  branding: any;
  hasTarionNumbers: boolean;
  onClickAttachment: (serviceRequestId: string) => void;
  // Path the SR detail page's back-breadcrumb should return to. Defaults
  // to the global SR list; per-page consumers (Property, Suite) override
  // so back navigates to the originating tab.
  navigateTo?: string;
  // Label rendered on the SR detail page's back-breadcrumb. When omitted
  // the SR page falls back to the generic "Service Requests" label.
  navLabel?: string;
  // Internal-only columns (internal/external Visibility + Days Open) are shown only to
  // internal/staff roles; hidden from external parties (home owners, realtors, occupants).
  canSeeInternalColumns: boolean;
}

// Accessors of the internal-only columns gated by canSeeInternalColumns.
export const INTERNAL_ONLY_COLUMN_ACCESSORS: string[] = [
  'isExternallyVisible',
  'daysOpen',
];

export const createServiceRequestTableColumns = ({
  allSelected,
  onSelectAll,
  expandedGroups,
  setExpandedGroups,
  selectedServiceRequests,
  onSelectGroup,
  onDeleteGroup,
  onSelectServiceRequest,
  onRemoveFromGroup,
  branding,
  hasTarionNumbers,
  onClickAttachment,
  navigateTo = PATHS.SERVICE_REQUESTS_LIST,
  navLabel,
  canSeeInternalColumns,
}: CreateTableColumnsParams): ITableColumn[] => {
  const baseColumns: ITableColumn[] = [
    {
      label: <Checkbox readOnly value={allSelected} onChange={onSelectAll} />,
      width: '8rem',
      sticky: true,
      accessor: 'selection',
      columnSelectionLabel: 'Select',
      columnSelectionDisabled: true,
      groupedHeaderCell: (data: any) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <GroupedHeaderControls
              data={data}
              onExpandGroup={() =>
                setExpandedGroups([...expandedGroups, data.id])
              }
              onContractGroup={() =>
                setExpandedGroups(
                  expandedGroups.filter((id: any) => id !== data.id)
                )
              }
              isSelected={
                allSelected ||
                data.children?.every((c) =>
                  selectedServiceRequests.includes(c.id)
                )
              }
              onSelectGroup={() =>
                onSelectGroup(data.children?.map((c) => c.id))
              }
              onDeleteGroup={() => onDeleteGroup(data.id)}
            />
          </div>
        );
      },
      cell: (data: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {data.isChild && (
            <div
              style={{ cursor: 'pointer' }}
              onClick={() => onRemoveFromGroup(data.id, data.groupId)}
            >
              <FontAwesomeIcon
                glyph="object-ungroup"
                weight="regular"
                size="big"
              />
            </div>
          )}
          <Checkbox
            checked={selectedServiceRequests.includes(data.id) || allSelected}
            name={data.id}
            value={selectedServiceRequests.includes(data.id) || allSelected}
            onChange={() => onSelectServiceRequest(data.id)}
            readOnly
          />
        </div>
      ),
    },
    {
      label: `${branding?.text?.suite}`,
      accessor: 'name',
      sortable: true,
      sticky: true,
      columnSelectionDisabled: true,
      groupedHeaderCell: (data) => {
        return (
          <>
            <span
              className="sr-cell-link"
              onClick={() => {
                setExpandedGroups(
                  expandedGroups.includes(data.id)
                    ? expandedGroups.filter((id: any) => id !== data.id)
                    : [...expandedGroups, data.id]
                );
              }}
            >
              {data.name}
            </span>

            {data.walkthroughPdfUrl && (
              <div
                style={{
                  cursor: 'pointer',
                  marginLeft: '1rem',
                  color: '#0a0a0a',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  whiteSpace: 'nowrap',
                }}
                title="Download Walkthrough PDF"
                onClick={(e) => {
                  e.stopPropagation();
                  ToastInfo(SuccessMessages.pdfDownloadNotification);
                  downloadDocument(data.walkthroughPdfUrl);
                }}
              >
                <FontAwesomeIcon glyph="download" weight="regular" />
              </div>
            )}
          </>
        );
      },
      minWidth: '12rem',
      width: '0.25fr',
      groupedHeaderCellSpan: hasTarionNumbers ? 10 : 9,
      xsGroupedHeaderCellSpan: hasTarionNumbers ? 10 : 9,
      cell: (data) => (
        <Link
          to={{
            pathname: PATHS.SERVICE_REQUESTS_VIEW.replace(':id', data.id),
            state: { navigateTo, navLabel },
          }}
        >
          {`${data.name}`}
        </Link>
      ),
    },
    {
      label: 'Strata Lot',
      accessor: 'stratalot',
      textOverflow: true,
      sortable: true,
      xsHide: false,
      minWidth: '14rem',
      columnDefaultSelection: true,
      cell: (data: any) => (
        <ExpandableText text={data.stratalot} characterLimit={24} />
      ),
    },
    {
      label: 'Description',
      accessor: 'description',
      textOverflow: true,
      sortable: true,
      xsHide: false,
      minWidth: '38rem',
      columnDefaultSelection: true,
      cell: (data: any) => <DescriptionCell serviceRequest={data} />,
    },
    {
      label: '📎',
      accessor: 'attachments',
      sortable: false,
      sticky: false,
      columnDefaultSelection: true,
      cell: (data) => {
        return (
          <Attachments
            attachments={{
              hasAttachment: data.hasAttachment,
              firstCompressedAttachment: data.firstCompressedAttachment,
              totalAttachments: data.totalAttachments,
            }}
            onClickAttachment={() => onClickAttachment(data.id)}
          />
        );
      },
      width: '7rem',
    },
    {
      label: 'Area',
      accessor: 'area',
      xsHide: false,
      width: '12rem',
      sortable: true,
      columnDefaultSelection: true,
      cell: (data: any) => (
        <ExpandableText text={data.area?.trim() ?? ''} characterLimit={24} />
      ),
    },
    {
      label: 'Asset',
      xsHide: false,
      accessor: 'item',
      columnDefaultSelection: true,
      width: '15rem',
      sortable: true,
      cell: (data: any) => (
        <ExpandableText text={data.item?.trim() ?? ''} characterLimit={30} />
      ),
    },
    {
      label: 'Type',
      xsHide: false,
      accessor: 'type',
      width: '11rem',
      sortable: true,
      columnDefaultSelection: true,
    },
    {
      label: 'Status',
      showOverflow: true,
      accessor: 'status',
      width: '15rem',
      xsHide: false,
      sortable: true,
      columnDefaultSelection: true,
      cell: (data: any) => (
        <Status
          icon={<IcoMoon glyph={ICONS[data.status]} />}
          label={displayStatusLabel(data.status, data.customLabel)}
        />
      ),
    },
    {
      label: 'Visibility',
      accessor: 'isExternallyVisible',
      sortable: true,
      xsHide: false,
      width: '12rem',
      columnDefaultSelection: true,
      cell: (data: any) =>
        data.isExternallyVisible === false ? 'Internal' : 'External',
    },
    {
      label: 'Submitted On',
      sortable: true,
      accessor: 'createdDate',
      sortingAlgorithm: sortByDate,
      cell: (data: any) => {
        const days: number = moment().diff(moment(data.createdDate), 'days');
        const color: 'teal' | 'yellow' | 'red' =
          days <= 30 ? 'teal' : days <= 60 ? 'yellow' : 'red';
        return (
          <Label color={color} label={convertTime(data.createdDate, 'date')} />
        );
      },
      columnDefaultSelection: true,
      width: '14rem',
    },
    {
      label: 'Days Open',
      accessor: 'daysOpen',
      sortable: true,
      xsHide: false,
      width: '14rem',
      columnDefaultSelection: true,
      cell: (data: any) => {
        if (typeof data.daysOpen !== 'number') {
          return '';
        }

        const pill = getDaysOpenPill(data.daysOpen);

        return <CategoryPill text={pill.text} color={pill.color} size="small" />;
      },
    },
    {
      label: 'Assigned To',
      accessor: 'serviceProvider',
      width: '18rem',
      sortable: true,
      columnDefaultSelection: true,
    },
    {
      label: 'Scheduled',
      accessor: 'scheduledAt',
      sortable: true,
      xsHide: false,
      sortingAlgorithm: sortByDate,
      columnDefaultSelection: true,
      cell: (data: any) =>
        isValidDate(data.scheduledAt)
          ? convertTime(data.scheduledAt, 'date')
          : '',
      width: '10rem',
    },
    {
      label: 'Last Updated',
      accessor: 'updatedAt',
      sortable: true,
      xsHide: false,
      sortingAlgorithm: sortByDate,
      columnDefaultSelection: true,
      // Backend already converts updatedAt to the tenant time zone; render the wall-clock as-is (date + time).
      cell: (data: any) =>
        isValidDate(data.updatedAt)
          ? convertTime(data.updatedAt, 'MM/DD/YYYY HH:mm')
          : '',
      width: '14rem',
    },
    {
      label: 'Ticket #',
      accessor: 'ticketNumber',
      sortable: true,
      width: '12.5rem',
      cell: (data: any) =>
        data.ticketNumber ? (
          <Link
            className="sr-cell-link"
            to={{
              pathname: PATHS.SERVICE_REQUESTS_VIEW.replace(':id', data.id),
              state: { navigateTo, navLabel },
            }}
          >
            {data.ticketNumber}
          </Link>
        ) : (
          ''
        ),
    },
    {
      label: 'Primary Homeowner',
      accessor: 'primaryHomeownerLastName',
      sortable: true,
      width: '18rem',
      xsHide: false,
      cell: (data: any) =>
        data.primaryHomeownerLastName
          ? `${data.primaryHomeOwnerFirstName} ${data.primaryHomeownerLastName}`
          : '',
    },
    {
      label: 'Issue Type',
      accessor: 'issueType',
      sortable: true,
      width: '18rem',
      xsHide: false,
    },
    {
      label: 'Property',
      accessor: 'propertyName',
      sortable: true,
      width: '18rem',
      xsHide: false,
    },
    {
      label: 'Building',
      accessor: 'building',
      sortable: true,
      width: '18rem',
      xsHide: false,
      cell: (data: any) => data.building || 'N/A',
    },
    {
      label: 'Tags',
      accessor: 'tags',
      sortable: false,
      width: '18rem',
      xsHide: false,
      cell: (data: any) => {
        const parsedTags: string[] = parseTags(data.tags);
        return parsedTags.join(', ');
      },
    },
  ];

  // Add tarion column after Type column (position 6).
  const tarionColumn = {
    label: 'Tarion #',
    accessor: 'tarionItemNumber',
    sortable: true,
    xsHide: false,
    style: { width: '8rem' },
    minWidth: '8rem',
    columnDefaultSelection: true,
    cell: (data: any) => data.tarionItemNumber || '',
  };

  const columns: ITableColumn[] = hasTarionNumbers
    ? [
        ...baseColumns.slice(0, 6), // First 6 columns.
        tarionColumn, // New tarion column when it is needed.
        ...baseColumns.slice(6), // Remaining columns.
      ]
    : baseColumns;

  // Internal-only columns are hidden from external parties (home owners, realtors, occupants).
  if (canSeeInternalColumns) return columns;

  return columns.filter(
    (column) =>
      !INTERNAL_ONLY_COLUMN_ACCESSORS.includes(column.accessor as string)
  );
};
